<template>
  <div>
    <div>
      <medication-fluid-selector
        :selected="state.selectedMedication"
        :case-conf="emitter.caseConf"
        :medications="allMedications"
        :disabled="readonly"
        :show-fluids="false"
        @select="
          (treatmentType, treatmentId) => {
            if (treatmentType === 'MEDICATION') {
              selectMedication(treatmentId);
            }
          }
        "
      />
      <help-tooltip v-if="!state.selectedMedication" type="warning">
        An option must be selected. Data will not be saved.
      </help-tooltip>
      <help-tooltip v-if="hasMedicationError" type="error">
        <span v-for="error in medicationErrors" :key="error.message">{{ error.message }}<br></span>
      </help-tooltip>
    </div>
    <div>
      <el-select
        v-if="state.selectedMedication"
        v-close-on-zoom
        :value="state.selectedRoutes"
        placeholder="any route"
        :filterable="true"
        :multiple="true"
        :default-first-option="true"
        class="routes-select"
        :disabled="readonly"
        :class="{ error: hasRouteError }"
        @input="routeKeys => selectRoutes(routeKeys)"
      >
        <el-option
          v-for="route in availableRoutes"
          :key="route.key"
          :label="route.name.en"
          :value="route.key"
        />
      </el-select>
      <help-tooltip v-if="hasRouteError" type="error">
        <span v-for="error in routeErrors" :key="error.message">{{ error.message }}<br></span>
      </help-tooltip>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import defaultRoutes from '~lib/common/config/routes';
import MedicationFluidSelector from './common/MedicationFluidSelector';

export default {
  name: 'medication-action',
  components: {
    HelpTooltip,
    MedicationFluidSelector,
  },
  props: [
    'medications',
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
  ],
  data() {
    return {
      state: {
        selectedMedication: undefined,
        selectedRoutes: [],
      },
      value: 0,
    };
  },
  computed: {
    allMedications() {
      const { isGlobal, caseConf } = this.emitter;
      if (isGlobal) {
        return this.medications;
      }
      return this.medications.concat(caseConf.customMedications || []);
    },
    availableDosageFormsByMedicationId() {
      return keyBy(
        this.availableDosageForms.map(caseDosageForm => ({
          ...caseDosageForm,
          enabledRoutes: orderBy(caseDosageForm.enabledRoutes, 'name.en'),
        })),
        'medicationId',
      );
    },
    availableDosageForms() {
      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return this.medications;
      }
      const { dosageForms } = this.emitter.caseConf;
      return dosageForms || [];
    },
    availableRoutes() {
      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return defaultRoutes;
      }
      if (
        !this.state.selectedMedication ||
        !this.availableDosageFormsByMedicationId[this.state.selectedMedication]
      ) {
        return [];
      }
      const { caseConf } = this.emitter;
      if (!caseConf.dosageForms) {
        return [];
      }
      const caseDosageForm = caseConf.dosageForms.find(caseDosageForm => caseDosageForm.medicationId === this.state.selectedMedication);
      if (!caseDosageForm) {
        return [];
      }
      const { enabledRoutes } = caseDosageForm;
      const results = defaultRoutes.filter(a =>
        enabledRoutes.some(b => a.key === b.key) && a.key !== 'IV_IO_INFUSION'
      );
      return results;
    },
    errors() {
      if (!this.emitter.$store) {
        return [];
      }
      const errors = get(
        this.emitter.$store.getters['validation/errorsByFieldAndId'],
        `caseLogicDescription.${this.control.parent.id}`,
        [],
      ).filter(error => error.metadata && error.metadata.scope === 'control');

      return uniqBy(errors, 'message');
    },
    medicationErrors() {
      return this.errors.filter(error => !error.metadata.routeId);
    },
    routeErrors() {
      return this.errors.filter(error => error.metadata.routeId);
    },
    hasMedicationError() {
      return !!this.medicationErrors.length;
    },
    hasRouteError() {
      return !!this.routeErrors.length;
    },
  },
  beforeMount() {
    const data = this.getData(this.ikey);
    this.state.selectedMedication = get(data, 'id');
    this.state.selectedRoutes = get(data, 'routes') || [];
    this.emitter.trigger('process', this.control);
  },
  methods: {
    selectMedication(medicationId) {
      this.state.selectedMedication = medicationId;
      this.state.selectedRoutes = this.state.selectedRoutes.filter(a =>
        this.availableRoutes.some(b => a === b.key),
      );
      this.update();
    },
    selectRoutes(routeKeys) {
      this.state.selectedRoutes = routeKeys;
      this.update();
    },
    update() {
      const { selectedMedication, selectedRoutes } = this.state;
      this.putData(this.ikey, {
        type: 'MEDICATION',
        id: selectedMedication,
        routes: selectedRoutes,
      });
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style>
.el-select.error {
  border: solid 3px #f92c2d;
}
</style>
